import React from 'react';
import classNames from 'classnames/bind';
import { Button, ButtonText } from '@saucelabs/ds-button';
import { OverlayTrigger, Tooltip } from '@saucelabs/sl-component-overlay';
import { Map } from 'immutable';
import { Typography } from '@saucelabs/ds-typography';
import { useFlags } from '@saucelabs/sl-util-launchdarkly';

import { Plan } from '../records/Plan';
import { PlanConfiguration } from '../records/PlanConfiguration';
import { Subscription } from '../records/Subscription';
import AnnualNudgeModal from './AnnualNudgeModal';
import { orderItemsToSubscriptionPreviews } from '../ducks/plans/utils';
import {
  calculateSavings,
  findOtherIntervalPlan,
} from '../ducks/purchase/utils';
import { amountSeparator, formatThousand } from '../utils/numbers';
import { projectsToMonthlyExecutions } from '../utils/plans';
import { planToProduct } from '../utils';
import InfoIcon from '../images/question_mark.svg';
import {
  CONCURRENCY_LABELS,
  MOBILE_QUALITY_LABEL,
  PLAN_CODE_INTERNVAL,
  PLAN_INTERVAL,
  PRODUCT_API_TESTING,
  PRODUCT_LIVE,
  PRODUCT_MOBILE_QUALITY,
  PRODUCT_RDC,
  PRODUCT_USAGE,
  PRODUCT_VDC,
  REAL_DEVICE,
  ROUTE_PARENT_SECTION,
  ROUTE_SECTION_CHECKOUT,
  ROUTE_SECTION_PURCHASE,
  TESTING_TYPES,
  TESTING_TYPE_TO_LABEL,
  UNIFIED_BILLING_TYPE,
  USAGE_TYPE,
  VIRTUAL_DEVICE,
} from '../constants';
import Styles from './OrderSummary.styles';

interface OrderSummaryProps {
  allPlans: Plan[];
  couponSavings?: number;
  currentMonthlyRate?: number;
  currentSubscriptions?: Subscription[];
  isContinueBlocked?: boolean;
  isPurchase?: boolean;
  onContinueClick?: () => void;
  onCancelClick?: void;
  orderItems: Map<string, PlanConfiguration>;
  showAnnualNudgeModal: () => void;
  taxAmount?: number;
  totalDueToday?: number;
  updateToAnnual?: () => void;
}

const context = classNames.bind(Styles);

const PLAN_LIVE_ANNUAL = `unified-${PRODUCT_LIVE}-${PLAN_CODE_INTERNVAL.ANNUAL}`;
const PLAN_LIVE_MONTHLY = `unified-${PRODUCT_LIVE}-${PLAN_CODE_INTERNVAL.MONTHLY}`;
const OrderSummary: React.FC<OrderSummaryProps> = ({
  allPlans,
  currentMonthlyRate = 0,
  couponSavings = 0,
  currentSubscriptions = [],
  isContinueBlocked = false,
  isPurchase = false,
  onCancelClick = () => null,
  onContinueClick = () => null,
  orderItems,
  showAnnualNudgeModal = () => null,
  taxAmount = 0,
  totalDueToday = 0,
  updateToAnnual = () => null,
}) => {
  const flags = useFlags();

  const productTypes = orderItems.keySeq().toArray();
  const getPlan = (planType: string) => (
    allPlans.find((plan) => plan.plan_code === `${planType !== PRODUCT_USAGE ? 'unified-' : ''}${planType}-${orderItems.get(planType).interval === PLAN_INTERVAL.MONTH ? PLAN_CODE_INTERNVAL.MONTHLY : PLAN_CODE_INTERNVAL.ANNUAL}`) as Plan
  );
  const subscriptionPreviews = orderItemsToSubscriptionPreviews(orderItems, allPlans);
  const getQuantity = (planType: string) => subscriptionPreviews[planType].productAmount as number;
  const getMonthlyRate = (planType: string) => subscriptionPreviews[planType].monthlyRate as number;
  const getPeriodRate = (planType: string) => subscriptionPreviews[planType].periodRate as number;

  const remainingMonthlyRate = currentSubscriptions.reduce(
    (prev: number, subscription: Subscription) => {
      const cost = subscription.getMonthlyPrice();
      // ignoring legacy since those would switch to unified even if user is just buying TestFairy
      const isSubscriptionStillActive = productTypes.includes(
        planToProduct(subscription.plan.plan_code),
      );

      const isAutomatedSubscription = [VIRTUAL_DEVICE, REAL_DEVICE].includes(
        subscription.plan.device_type,
      );
      const isLiveSubscription = [PLAN_LIVE_ANNUAL, PLAN_LIVE_MONTHLY].includes(
        subscription.plan.plan_code,
      );
      const isBuyingLive = productTypes.includes(PRODUCT_LIVE);
      const isBuyingAutomated = productTypes.includes(PRODUCT_VDC) ||
        productTypes.includes(PRODUCT_RDC);

      // Ignore any non unified-billing / usage subscription
      // and ignore switching between live <-> vdc/rdc
      const shouldIgnoreSubscription = ![USAGE_TYPE, UNIFIED_BILLING_TYPE].includes(
        subscription.plan.plan_type,
      ) ||
        (isLiveSubscription && isBuyingAutomated) ||
        (isAutomatedSubscription && isBuyingLive);

      if (shouldIgnoreSubscription || isSubscriptionStillActive) return prev;

      return prev + cost;
    }, 0);

  interface Summary {
    dueToday: number;
    newAnnualRate: number;
    newMonthlyRate: number;
    potentialSavings: number;
  }

  const summary = productTypes.reduce(
    (acc: Summary, planType: string) => {
      const plan = getPlan(planType);
      const quantity = getQuantity(planType);
      const monthlyRate = getMonthlyRate(planType);

      // Allow graceful fail when there was an issue getting plans from backend
      if (!plan) {
        return acc;
      }
      if (plan.interval_unit === PLAN_INTERVAL.YEAR) {
        acc.newMonthlyRate += monthlyRate;
        acc.newAnnualRate += getPeriodRate(planType);
      }
      if (plan.interval_unit === PLAN_INTERVAL.MONTH) {
        acc.newMonthlyRate += monthlyRate;
        acc.newAnnualRate += 12 * monthlyRate;
        acc.potentialSavings += (
          calculateSavings(plan, quantity, findOtherIntervalPlan(plan, allPlans))
        );
      }
      acc.dueToday += plan.price;
      if (plan.add_ons?.length) {
        acc.dueToday += plan.add_ons[0].calculatePrice(quantity);
      }

      return acc;
    },
    {
      dueToday: 0,
      newAnnualRate: 12 * remainingMonthlyRate || 0,
      newMonthlyRate: remainingMonthlyRate || 0,
      potentialSavings: 0,
    },
  );

  const crossBrowserProductTypes = productTypes.filter(
    (elem) => ([PRODUCT_LIVE, PRODUCT_RDC, PRODUCT_VDC]).includes(elem),
  );
  const crossBrowserSection = crossBrowserProductTypes.length > 0 && (
    <div className={context('order-items', { 'no-horizontal-margin': isPurchase })}>
      {crossBrowserProductTypes.map((planType) => (
        <div
          key={planType}
          data-qa={planType}
        >
          <p className={Styles['order-product-type']}>
            {planType === PRODUCT_LIVE ? 'Live' : 'Automated + Live'}
            {' '}
            Testing
          </p>
          <p className={Styles['order-line']}>
            <span>
              {getQuantity(planType)}
              {' '}
              {CONCURRENCY_LABELS[planType]}
            </span>
            <span>
              {`${amountSeparator(getMonthlyRate(planType))}/mo`}
            </span>
          </p>
        </div>
      ))}
    </div>
  );

  const hasMobileQuality = productTypes.includes(PRODUCT_MOBILE_QUALITY);
  const mobileQualitySection = hasMobileQuality && (
    <div className={context('order-items', { 'no-horizontal-margin': isPurchase })}>
      <Typography
        className={`${Styles['order-product-type']} mt-0`}
        disableSpacing
        variant="heading-5"
      >
        {MOBILE_QUALITY_LABEL}
      </Typography>
      <div
        key={PRODUCT_MOBILE_QUALITY}
        data-qa={PRODUCT_MOBILE_QUALITY}
      >
        <p className={Styles['order-line']}>
          <span>
            Startup
          </span>
          <span>
            {`${amountSeparator(getMonthlyRate(PRODUCT_MOBILE_QUALITY))}/mo`}
          </span>
        </p>
      </div>
    </div>
  );

  const hasApiTesting = productTypes.includes(PRODUCT_API_TESTING);
  const apiTestingSection = hasApiTesting && (
    <div className={context('order-items', { 'no-horizontal-margin': isPurchase })}>
      <Typography
        className={`${Styles['order-product-type']} mt-0`}
        disableSpacing
        variant="heading-5"
      >
        {TESTING_TYPE_TO_LABEL[TESTING_TYPES.API_TESTING]}
      </Typography>
      <div
        key={PRODUCT_API_TESTING}
        data-qa={PRODUCT_API_TESTING}
      >
        <p className={Styles['order-line']}>
          <span>
            {getQuantity(PRODUCT_API_TESTING)}
            {' projects, '}
            {formatThousand(projectsToMonthlyExecutions(
              getQuantity(PRODUCT_API_TESTING),
              getPlan(PRODUCT_API_TESTING).add_ons,
            ))}
            {' executions/mo'}
          </span>
          <span>
            {`${amountSeparator(getMonthlyRate(PRODUCT_API_TESTING))}/mo`}
          </span>
        </p>
      </div>
    </div>
  );

  const hasUsage = productTypes.includes(PRODUCT_USAGE);
  const creditsPlanInterval = subscriptionPreviews[PRODUCT_USAGE]?.interval;
  const usageSection = hasUsage && (
    <div className={context('order-items', { 'no-horizontal-margin': isPurchase })}>
      <Typography
        className="font-bold text-high-emphasis m-0"
        disableSpacing
        variant="heading-5"
      >
        {TESTING_TYPE_TO_LABEL[TESTING_TYPES.USAGE]}
      </Typography>
      <div
        key={PRODUCT_USAGE}
        data-qa={PRODUCT_USAGE}
      >
        <p className={Styles['order-line']}>
          <span>
            {formatThousand(getQuantity(PRODUCT_USAGE), 'standard')}
            {` credits/${creditsPlanInterval === PLAN_INTERVAL.YEAR ? 'yr' : 'mo'} `}
          </span>
          <span>
            {`${amountSeparator(getPeriodRate(PRODUCT_USAGE))}/${creditsPlanInterval === PLAN_INTERVAL.YEAR ? 'yr' : 'mo'}`}
          </span>
        </p>
      </div>
    </div>
  );

  // The only special case where we present amounts per year instead of per month:
  const isUsageAnnual = hasUsage && creditsPlanInterval === PLAN_INTERVAL.YEAR;
  return (
    <div>
      {crossBrowserSection}
      {apiTestingSection}
      {mobileQualitySection}
      {usageSection}
      {currentMonthlyRate > 0 && (
        <p
          className={context('order-summary-line', { 'no-horizontal-margin': isPurchase })}
          data-qa="current-monthly-rate"
        >
          <span>Current Monthly Rate</span>
          <span>{amountSeparator(currentMonthlyRate)}</span>
        </p>
      )}
      <p
        className={context('order-summary-line', { 'no-horizontal-margin': isPurchase })}
        data-qa="new-monthly-rate"
      >
        <strong>
          {currentMonthlyRate > 0 && 'New '}
          {`${isUsageAnnual ? 'Annual' : 'Monthly'} Rate`}
        </strong>
        <strong>{amountSeparator(summary[isUsageAnnual ? 'newAnnualRate' : 'newMonthlyRate'])}</strong>
      </p>
      {!hasUsage && (
        <p
          className={context('order-summary-line', { 'no-horizontal-margin': isPurchase })}
          data-qa="estimated-due-today"
        >
          <strong>
            Estimated Due Today
            <OverlayTrigger
              delayHide={300}
              overlay={(
                <Tooltip id="estimated-month-rate-tooltip">
                  For annual plans, you will be charged for the year up front.
                  This is an estimate of how much you will have to pay today,
                  excluding taxes and prorated sum from current subscription
                  balance.
                </Tooltip>
              )}
              placement="top"
            >
              <InfoIcon className={Styles['info-icon']} />
            </OverlayTrigger>
          </strong>
          <strong>{amountSeparator(summary.dueToday)}</strong>
        </p>
      )}
      {!isPurchase ? (
        <>
          {flags?.growthAnnualPlanNudge && (
            <AnnualNudgeModal
              annualSavings={summary.potentialSavings}
              onContinue={onContinueClick}
              updateToAnnual={updateToAnnual}
            />
          )}
          <hr className={Styles['container-break']} />
          <div className={Styles['order-buttons']}>
            <ButtonText
              className={[
                Styles['order-summary-button'],
                Styles['order-cancel'],
              ].join(' ')}
              color="primary"
              data-qa="cancel-button"
              onPress={onCancelClick}
              to={currentMonthlyRate ?
                `/${ROUTE_PARENT_SECTION}` : `/${ROUTE_PARENT_SECTION}/${ROUTE_SECTION_PURCHASE}`}
            >
              Cancel
            </ButtonText>
            {flags?.growthAnnualPlanNudge ? (
              <Button
                className={Styles['order-summary-button']}
                color="primary"
                data-qa="continue-button"
                disabled={isContinueBlocked}
                onPress={showAnnualNudgeModal}
                variant="contained"
              >
                <strong>Continue</strong>
              </Button>
            ) : (
              <ButtonText
                className={Styles['order-summary-button']}
                color="primary"
                data-qa="continue-button"
                disabled={isContinueBlocked}
                elementType="button"
                onPress={onContinueClick}
                to={`/${ROUTE_PARENT_SECTION}/${ROUTE_SECTION_CHECKOUT}`}
                variant="contained"
              >
                <strong>Continue</strong>
              </ButtonText>

            )}
          </div>
        </>
      ) : (
        <>
          <p
            className={context('order-summary-line', { 'no-horizontal-margin': isPurchase })}
            data-qa="taxes-and-fees"
          >
            <span>Taxes and Fees</span>
            <span>{taxAmount ? amountSeparator(taxAmount) : '--'}</span>
          </p>
          <p
            className={context('order-summary-line', { 'no-horizontal-margin': isPurchase })}
            data-qa="coupon-savings"
          >
            <span>Coupon Code Savings</span>
            <span>{couponSavings ? amountSeparator(couponSavings) : '--'}</span>
          </p>
          <br />
          <Typography
            className={context('order-summary-line', { 'no-horizontal-margin': isPurchase })}
            data-qa="due-today"
            disableSpacing
            variant="heading-4"
          >
            <strong>Due Today</strong>
            <strong>
              {totalDueToday !== 0 ? amountSeparator(totalDueToday) : '--'}
            </strong>
          </Typography>
        </>
      )}
    </div>
  );
};

export { OrderSummary };
